import { Button, makeStyles, tokens, mergeClasses } from "@fluentui/react-components";
import { 
    HomeRegular, 
    DataUsageRegular, 
    WarningRegular, 
    ChatRegular,
    NavigationRegular,
    BoxMultipleRegular,
    DocumentEditRegular,
    ErrorCircleRegular
} from "@fluentui/react-icons";
import { useNavigate, useLocation } from "react-router-dom";
import { useState } from "react";
import Configuration from "../../config/einvoicing-configuration";
import { isManualInvoiceCreatorEnabled } from "../../pages/manual-invoice/featureGate";

interface NavigationProps {
    isAdmin: boolean;
}

const useStyles = makeStyles({
    container: {
        height: '100%',
        borderRight: `1px solid ${tokens.colorNeutralStroke1}`,
        display: 'flex',
        flexDirection: 'column',
        paddingTop: '0.625rem',
        transition: 'width 0.2s',
        backgroundColor: tokens.colorNeutralBackground1,
    },
    collapsed: {
        width: '3rem',
        alignItems: 'center',
    },
    expanded: {
        width: '12.5rem',
        alignItems: 'stretch',
    },
    toggleButton: {
        marginBottom: '0.625rem',
        display: 'flex',
        justifyContent: 'center',
        minHeight: '2rem',
    },
    toggleButtonExpanded: {
        justifyContent: 'flex-start',
        paddingLeft: '0.625rem',
    },
    navList: {
        listStyle: 'none',
        padding: '0',
        margin: '0',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
    },
    navItem: {
        width: '100%',
    },
    navButton: {
        width: '100%',
        justifyContent: 'center',
        minHeight: '36px',
    },
    navButtonExpanded: {
        justifyContent: 'flex-start',
        paddingLeft: '10px',
    },
    navButtonSelected: {
        backgroundColor: tokens.colorNeutralBackground1Selected,
    },
});

function Navigation(props: NavigationProps) {
    const navigate = useNavigate();
    const location = useLocation();
    const styles = useStyles();
    const [isOpen, setIsOpen] = useState(false);

    const getSelectedValue = () => {
        const path = location.pathname;
        if (path === "/") return "/";
        if (path.startsWith("/latency")) return "/latency";
        if (path.startsWith("/failure-analysis")) return "/failure-analysis";
        if (path.startsWith("/batches")) return "/batches";
        if (path.startsWith("/reports")) return "/reports";
        if (path.startsWith("/chat")) return "/chat";
        if (path.startsWith("/manual-invoice")) return "/manual-invoice";
        return "/";
    };

    const selectedValue = getSelectedValue();

    const manualInvoiceCreatorEnabled = isManualInvoiceCreatorEnabled(Configuration);

    const navItems = [
        { value: "/", icon: <HomeRegular />, label: "Home" },
        { value: "/latency", icon: <DataUsageRegular />, label: "Latency / SLA" },
        { value: "/batches", icon: <BoxMultipleRegular />, label: "Batches" },
        ...(manualInvoiceCreatorEnabled ? [{ value: "/manual-invoice", icon: <DocumentEditRegular />, label: "Manual Invoice Creator" }] : []),
        { value: "/failure-analysis", icon: <ErrorCircleRegular />, label: "Failure Analysis" },
        ...(props.isAdmin ? [{ value: "/reports", icon: <WarningRegular />, label: "Reports" }] : []),
        ...(props.isAdmin ? [{ value: "/chat", icon: <ChatRegular />, label: "AI Chat" }] : []),
    ];

    return (
        <nav aria-label="Main navigation" className={mergeClasses(styles.container, isOpen ? styles.expanded : styles.collapsed)}>
            <div className={mergeClasses(styles.toggleButton, isOpen && styles.toggleButtonExpanded)}>
                <Button 
                    appearance="subtle" 
                    icon={<NavigationRegular />} 
                    onClick={() => setIsOpen(!isOpen)}
                    title={isOpen ? "Collapse Navigation" : "Expand Navigation"}
                    aria-label={isOpen ? "Collapse Navigation" : "Expand Navigation"}
                    aria-description="Toggle the navigation sidebar between expanded and collapsed views"
                    aria-expanded={isOpen}
                />
            </div>
            <ul className={styles.navList}>
                {navItems.map((item) => (
                    <li key={item.value} className={styles.navItem}>
                        <Button
                            appearance="subtle"
                            icon={item.icon}
                            onClick={() => navigate(item.value)}
                            aria-label={item.label}
                            aria-current={selectedValue === item.value ? "page" : undefined}
                            className={mergeClasses(
                                styles.navButton,
                                isOpen && styles.navButtonExpanded,
                                selectedValue === item.value && styles.navButtonSelected
                            )}
                        >
                            {isOpen && item.label}
                        </Button>
                    </li>
                ))}
            </ul>
        </nav>
    );
}

export default Navigation;